import React, { useEffect, useState, useRef, useCallback } from 'react';
import { NavDropdown, Badge, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/free-solid-svg-icons';
import NotificationService from '../../services/notification-service';
import './Notification.scss';

export default function NotificationDropdown({
  userId,
  pollInterval = 15000,
  markReadOnOpen = false, // retained for API compatibility
  toggleContent = null,
  role,
  menuMaxHeight = 50, // vh
}) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const mounted = useRef(true);

  // Single persistent service instance used everywhere in this component
  const notificationService = useRef(new NotificationService());

  // track IDs currently being marked so we don't send duplicate requests
  const inProgress = useRef(new Set());

  // helper to parse dates from notification objects
  function parseNotificationDate(notificationItem) {
    if (notificationItem.createdAt) return new Date(notificationItem.createdAt);
    if (notificationItem.data && notificationItem.data.listingDate) return new Date(notificationItem.data.listingDate);

    const m = String(notificationItem.id || '').match(/:(\d{4}-\d{2}-\d{2})$/);
    if (m) return new Date(m[1]);
    return new Date(); // fallback
  }

  useEffect(() => {
    mounted.current = true;

    async function load() {
      const svc = notificationService.current;
      if (svc._inflight) return;
      svc._inflight = true;
      setLoading(true);

      try {
        const res = await svc.getNotifications(userId, { limit: 5, role });
        if (!mounted.current) return;
        const arr = res?.notifications || [];
        const normalized = arr.map(notificationItem => ({
          ...notificationItem,
          // ensure createdAt is an ISO-ish string if present so parseNotificationDate works consistently
          createdAt: notificationItem.createdAt || (notificationItem.data && notificationItem.data.listingDate) || undefined,
        }));
        normalized.sort((a, b) => parseNotificationDate(b) - parseNotificationDate(a));
        setNotifications(normalized);
      } catch (e) {
        console.error('Failed to load notifications', e);
      } finally {
        svc._inflight = false;
        if (mounted.current) setLoading(false);
      }
    }

    load();
    const id = setInterval(load, pollInterval);
    return () => { mounted.current = false; clearInterval(id); };
  }, [userId, pollInterval, role]);

  const unreadNotifications = notifications.filter(notificationItem => !notificationItem.isRead);
  const readNotifications = notifications.filter(notificationItem => !!notificationItem.isRead);
  const unreadCount = unreadNotifications.length;

  // minimal toggle handler — do NOT mark as read here (per requirements)
  const onToggle = (isOpen) => {
    setOpen(isOpen);
  };

  // Helper to determine whether the service response is a success.
  function isMarkSuccess(res) {
    if (!res) return false;
    if (res.status === "200") return true;
    if (res.notification && res.notification.id) return true;
    return false;
  }

  // mark single notification read when user clicks it.
  // returns true on success, false on failure.
  const markSingleRead = useCallback(async (id, link) => {
    const svc = notificationService.current;
    if (!svc || typeof svc.markAsRead !== 'function') {
      console.warn('Notification service not initialized or missing markAsRead');
      return false;
    }

    // prevent duplicate requests for same id
    if (inProgress.current.has(id)) return false;
    inProgress.current.add(id);

    // deep clone snapshot so we can revert reliably if something fails
    const snapshot = JSON.parse(JSON.stringify(notifications));

    // optimistic update: mark the item as read locally
    setNotifications(prev => prev.map(item =>
      item.id === id ? { ...item, isRead: true, readAt: new Date().toISOString() } : item
    ));

    try {
      const res = await svc.markAsRead(id);

      // check for success in a few common shapes
      const ok = isMarkSuccess(res);

      if (!ok) {
        throw new Error('markAsRead did not return a success response');
      }

      // if server returned updated notification object, merge it in
      const updated = res?.notification;
      if (updated && updated.id) {
        setNotifications(cur => cur.map(item => item.id === updated.id ? { ...item, ...updated } : item));
      }

      return true;
    } catch (e) {
      console.error('Failed to mark notification as read', e);
      // revert optimistic update
      setNotifications(snapshot);
      return false;
    } finally {
      inProgress.current.delete(id);
    }
  }, [notifications]);

  const renderItem = (notificationItem) => {
    const when = parseNotificationDate(notificationItem);
    const label = notificationItem.data?.listingDate ? `Listing: ${notificationItem.data.listingDate}` : (notificationItem.subject || notificationItem.message || '');
    const link = notificationItem.link || '';

    return (
      <NavDropdown.Item
        key={notificationItem.id}
        href={notificationItem.link || '#'}
        onClick={async (ev) => {
          // Respect modifier keys (ctrl/meta) for opening in new tab/window.
          const isModifier = ev.ctrlKey || ev.metaKey || ev.shiftKey || (ev.button && ev.button === 1);

          if (!link || link === '#') {
            // No navigation target; mark and return.
            ev.preventDefault();
            ev.stopPropagation();
            if (!notificationItem.isRead) {
              markSingleRead(notificationItem.id, link).catch(() => {});
            }
            return;
          }

          if (isModifier) {
            // Let default action happen for new-tab/new-window — but still attempt to mark (fire-and-forget).
            if (!notificationItem.isRead) {
              markSingleRead(notificationItem.id, link).catch(() => {});
            }
            // Don't prevent default so browser will open new tab/window.
            return;
          }

          // regular single-click navigation: prevent bootstrap/default closing and handle marking first
          ev.preventDefault();
          ev.stopPropagation();

          if (notificationItem.isRead) {
            window.location.href = link;
            return;
          }

          const ok = await markSingleRead(notificationItem.id, link);
          if (ok) {
            window.location.href = link;
          } else {
            // failed: don't navigate; you could add a toast here
            console.error('Could not mark notification as read — navigation cancelled.');
          }
        }}
        className={`notification-item ${notificationItem.isRead ? 'read' : 'unread'}`}
      >
        <div className="d-flex w-100 justify-content-between align-items-start">
          <div>
            <div className="notif-subject">{label}</div>
            {notificationItem.summary && <div className="small text-muted">{notificationItem.summary}</div>}
            <div className="notif-meta small text-muted">{when.toLocaleString()}</div>
          </div>
          {!notificationItem.isRead && <div className="unread-dot" aria-hidden="true" />}
        </div>
      </NavDropdown.Item>
    );
  };

  const defaultToggle = (
    <>
      <FontAwesomeIcon icon={faBell} className="bell-icon" />
      {unreadCount > 0 && <Badge bg="danger" className="ms-1">{unreadCount}</Badge>}
    </>
  );

  return (
    <NavDropdown
      title={toggleContent || defaultToggle}
      id="collasible-nav-dropdown"
      align="end"
      onToggle={onToggle}
      className="notification-dropdown"
    >
      {/* Header remains outside the scrollable body so it stays visible */}
      <div className="notif-header px-3">
        <strong>Notifications</strong>
        {loading && <Spinner animation="border" size="sm" className="ms-2" />}
      </div>

      <NavDropdown.Divider />

      {/* Scrollable body */}
      <div className="notif-body" style={{ maxHeight: typeof menuMaxHeight === 'number' ? `${menuMaxHeight}vh` : menuMaxHeight }}>
        {/* Unread section */}
        {unreadNotifications.length > 0 && (
          <>
            <div className="section-header px-3">
              <small className="text-uppercase">Today</small>
            </div>
            {unreadNotifications.map(renderItem)}
            <NavDropdown.Divider />
          </>
        )}

        {/* Read / earlier */}
        <div className="section-header px-3">
          <small className="text-uppercase">{readNotifications.length > 0 ? 'Earlier' : 'No more notifications'}</small>
        </div>

        {readNotifications.length === 0 && !loading && unreadNotifications.length === 0 && (
          <NavDropdown.ItemText className="small text-muted px-3">No notifications</NavDropdown.ItemText>
        )}

        {readNotifications.map(renderItem)}
      </div>
    </NavDropdown>
  );
}
